import { ChevronRight, ChevronLeft } from "lucide-react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import { cn } from "@/lib/utils";
import { useRef, useState } from "react";

import { ProjectDetailProps } from "@/type/ProjectTypes";
import imgUrlMapper from "@/utils/imgUrl-mapping";
import { DateUtils } from "@/utils/dateUtil";
import { useNavigate } from "react-router-dom";
import SwiperNavigation from "./swiper-navigation";

export default function CarouselOrientation({
  projectlist,
}: {
  projectlist: Omit<ProjectDetailProps, "project_pin">[];
}) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const nav = useNavigate();
  const [current, setCurrent] = useState(1); // 현재 위치

  return (
    <div className="relative lg:flex items-start gap-20">
      {/* Prev Button */}

      {/* Swiper */}
      <button
        ref={prevRef}
        className={cn(
          " z-20 p-2 rounded-full absolute top-1/2 -translate-1/2 -left-5    hover:border-zinc-300/30",
          isAtStart && "opacity-60 pointer-events-none"
        )}
      >
        <ChevronLeft className="size-10 text-zinc-50/80" strokeWidth={1} />
      </button>

      {/* Next Button */}
      <button
        ref={nextRef}
        className={cn(
          " z-10 p-2 rounded-full absolute -right-20 top-1/2 -translate-1/2    hover:border-zinc-300/90",
          isAtEnd && "opacity-60 pointer-events-none"
        )}
      >
        <ChevronRight className="size-10 text-zinc-50/80" strokeWidth={1} />
      </button>

      <div className="w-full">
        <Swiper
          speed={1000}
          spaceBetween={20}
          modules={[Navigation]}
          onBeforeInit={(swiper) => {
            if (typeof swiper.params.navigation !== "boolean") {
              swiper.params.navigation!.prevEl = prevRef.current;
              swiper.params.navigation!.nextEl = nextRef.current;
            }
          }}
          onSlideChange={(sw) => setCurrent(sw.realIndex + 1)}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onReachBeginning={() => setIsAtStart(true)}
          onReachEnd={() => setIsAtEnd(true)}
          onFromEdge={() => {
            setIsAtStart(false);
            setIsAtEnd(false);
          }}
          breakpoints={{
            320: { slidesPerView: 1.1 },
            768: { slidesPerView: 1 },
          }}
          className="overflow-visible! md:overflow-hidden!"
        >
          {projectlist.map((project, idx) => (
            <SwiperSlide key={idx}>
              {({ isActive }) => {
                const stack = project.project_meta_stack.flatMap(
                  (e) => e.project_stack
                );
                const frameWork = stack.find(
                  (e) =>
                    e.type === "framework" &&
                    (e.stack === "React" || e.stack === "Next.js")
                );
                return (
                  <div
                    key={project.id}
                    onClick={() => nav(`/work/${project.id}`)}
                    className={cn(
                      "group cursor-pointer grid  md:grid-cols-[2fr_2fr] gap-10 w-full rounded-xl transition-all duration-500",
                      isActive
                        ? "opacity-100 grayscale-0 "
                        : "opacity-50 grayscale"
                    )}
                  >
                    <div className="lg:aspect-8/5 aspect-9/8 rounded-lg relative overflow-hidden  transition-all   ">
                      <div
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.05]"
                        style={{
                          backgroundImage: `url(${imgUrlMapper({ thumbnail: project.thumbnail })})`,
                        }}
                      />
                    </div>
                    <div
                      className={cn(
                        "flex flex-col gap-2 md:py-15 transition-all",
                        isActive ? "opacity-100" : "opacity-0"
                      )}
                    >
                      <span className="text-xs text-pink-400">
                        {frameWork?.stack}
                      </span>
                      <h3 className="text-3xl font-semibold tracking-tight text-zinc-50">
                        {project.title}
                      </h3>
                      <p className="text-sm text-zinc-400 my-2 leading-relaxed line-clamp-2 max-w-[400px] break-keep">
                        {project.description}
                      </p>
                      <div className=" items-center gap-4 text-[0.85rem] text-zinc-400 mt-3">
                        <span>{project.project_member}</span>
                        <span className="opacity-40">•</span>
                        <span>
                          {DateUtils.getDurationDays(
                            project.start_date!,
                            project.end_date!
                          )}
                          일
                        </span>
                      </div>
                    </div>{" "}
                  </div>
                );
              }}
            </SwiperSlide>
          ))}
          <SwiperNavigation activeIdx={current} length={projectlist.length} />
        </Swiper>
      </div>
    </div>
  );
}
